<template>
    <div>
        <FormulateForm v-model="formData" :errors="$page.props.errors" novalidate @submit="$emit('change', $event)">
            <div class="form_field">
                <IconProfile class="form_field_prefix" />
                <FormulateInput
                    type="text"
                    name="name"
                    validation="required"
                    placeholder="Business Name"
                />
            </div>
            <div class="form_field">
                <IconPhone class="form_field_prefix" />
                <FormulateInput
                    type="tel"
                    name="phone"
                    placeholder="Business Phone"
                    validation="phoneValidation"
                    :validation-rules="phoneValidationRules"
                    :validation-messages="phoneValidationMessages"
                >
                    <template #element="{ name }">
                        <vue-tel-input
                            v-model="formData.phone"
                            :name="name"
                            placeholder="Business Phone"
                            :auto-format="true"
                            :enabled-country-code="false"
                            default-country="us"
                            :dropdown-options="{
                                showDialCodeInSelection: true,
                                showDialCodeInList: true,
                                showSearchBox: true,
                                showFlags: false,
                            }"
                            :input-options="{
                                autocomplete: 'on',
                                type: 'tel',
                                showDialCode: false,
                                placeholder: 'Business Phone',
                            }"
                            :preferred-countries="['us', 'il']"
                            :valid-characters-only="true"
                            @validate="handleValidatePhone"
                        />
                    </template>
                </FormulateInput>
            </div>
            <div class="form_field">
                <IconEmail class="form_field_prefix" />
                <FormulateInput
                    type="email"
                    name="email"
                    validation="required|email"
                    placeholder="Business Email"
                />
            </div>
            <div class="form_field">
                <IconHome class="form_field_prefix" />
                <FormulateInput
                    type="url"
                    name="website"
                    validation="required|url"
                    placeholder="Website URL"
                    @blur="handleWebsiteBlur"
                />
            </div>
            <div class="form_field">
                <IconGlobal class="form_field_prefix" />
                <FormulateInput
                    name="country"
                    type="vue-select"
                    validation="required"
                    placeholder="Country"
                    :options="countries"
                    class="form_select"
                    :searchable="true"
                    :clearable="false"
                    :autoscroll="true"
                    :components="{ OpenIndicator }"
                />
            </div>
            <div class="form_field">
                <IconBriefcase class="form_field_prefix" />
                <FormulateInput
                    name="industry"
                    type="vue-select"
                    validation="required"
                    placeholder="Industry"
                    :options="industries"
                    class="form_select"
                    :searchable="true"
                    :clearable="false"
                    :autoscroll="true"
                    :components="{ OpenIndicator }"
                />
            </div>
            <FormulateInput type="submit" label="Next" class="submitButton" />
        </FormulateForm>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { SelectOption, OrganizationFormData } from '../types';
import IconEmail from '@/components/icons/IconEmail.vue';
import IconProfile from '@/components/icons/IconProfile.vue';
import IconPhone from '@/components/icons/IconPhone.vue';
import IconHome from '@/components/icons/IconHome.vue';
import IconBriefcase from '@/components/icons/IconBriefcase.vue';
import IconGlobal from '@/components/icons/IconGlobal.vue';

type FormData = Omit<OrganizationFormData, 'coupon' | 'plan_id'>

withDefaults(defineProps<{
    industries?: SelectOption[];
    countries?: SelectOption[];
}>(), {
    countries: () => [],
    industries: () => [],
});

defineEmits<{
    change: (data: FormData) => void;
}>();

const formData = ref<Partial<FormData>>({
    name: undefined,
    phone: undefined,
    email: undefined,
    website: undefined,
    country: undefined,
    industry: undefined,
});

const phoneValid = ref<boolean>(false);
const phoneValidationRules = { phoneValidation: ({ value }: { value: string }) => value?.length && phoneValid.value };
const phoneValidationMessages = { phoneValidation: () => 'Please enter a valid phone number' };

function handleValidatePhone({ valid }: { valid: boolean }) {
    phoneValid.value = valid;
}

function handleWebsiteBlur() {
    if (formData.value.website && !formData.value.website.startsWith('http')) {
        formData.value.website = `https://${formData.value.website}`;
    }
}

const OpenIndicator = ref({
    render: (createElement: any) =>
        createElement('span', { class: { toggle: true } }),
});
</script>

<style lang="scss">
.form_select {
    .vs__actions {
        position: static;
    }
    .vs__selected-options {
        .vs__selected {
            z-index: 2;
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            top: 0;
            padding: 18px 40px 18px 48px;
            color: #202020;
            font-family: "ITC Avant Garde Gothic Std",serif;
            font-size: 16px;
            font-style: normal;
            font-weight: 300;
            line-height: 160%;
            &::placeholder {
                color: #878c95;
            }
            @media screen and (max-width: 1540px) {
                padding: 16px 40px 16px 48px;
            }
        }
    }

    .vs__open-indicator {
        width: 24px;
        height: 24px;
        position: absolute;
        top: 16px;
        right: 0;

        &:before {
            content: "";
            position: absolute;
            left: 7px;
            top: 5px;
            width: 10px;
            height: 10px;
            border-bottom: 2px solid #292d32;
            border-right: 2px solid #292d32;
            transform: rotate(45deg);
            transition: all 0.3s;
        }
    }

    .v-select.vs--open {
        .vs__open-indicator:before {
            top: 9px;
            transform: rotate(225deg);
        }
    }
    .vs__dropdown-menu {
        z-index: 1000;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 100%;
        padding: 12px 0;
        border-radius: 30px;
        margin: 0;
        list-style: none;
        box-shadow: 0 20px 70px 0 rgba(0, 0, 0, 0.05);
        max-height: 340px;
        overflow: auto;
        background-color: #ffffff;

        &::-webkit-scrollbar {
            width: 4px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 2px;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: #555;
        }
    }
    .vs__dropdown-option {
        padding: 16px 48px;
        font-family: "ITC Avant Garde Gothic Std",serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 300;
        line-height: 175%;
        color: #878c95;
        background-color: transparent;
        transition: all 0.3s;

        @media screen and (max-width: 1100px) {
            font-size: 12px;
        }

        &.vs__dropdown-option--selected {
            color: #202020;
        }
        &.vs__dropdown-option--highlight {
            color: #202020;
            background-color: #e3f3ff;
        }
    }
}

.form_field {
    .vue-tel-input {
        outline: none;
        border: none;
        box-shadow: none;
        border-bottom: 1px solid #cdcbdf;
        padding: 18px 40px 18px 48px;

        input {
            padding: 0 8px;
            border: none;
        }
    }

    .vti__dropdown {
        padding: 0;
        background-color: transparent;
        &:hover {
            background-color: transparent;
        }
    }
    .vti__country-code {
        padding: 0;
        font-family: "ITC Avant Garde Gothic Std",serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 300;
        line-height: 160%;
        color: #202020;
    }
    .vti__dropdown-arrow {
        width: 24px;
        height: 24px;
        position: relative;
        font-size: 0;
        transform: scaleY(1);
        &:before {
            content: "";
            position: absolute;
            left: 8px;
            top: 6px;
            width: 8px;
            height: 8px;
            border-bottom: 2px solid #292d32;
            border-right: 2px solid #292d32;
            transform: rotate(45deg);
            transition: all 0.3s;
        }
    }

    .vti__dropdown-list {
        z-index: 1000;
        padding: 12px 0;
        border-radius: 30px;
        margin: 0;
        list-style: none;
        box-shadow: 0 20px 70px 0 rgba(0, 0, 0, 0.05);
        max-height: 340px;
        overflow: auto;
        background-color: #ffffff;
        width: 420px;
        border: none;

        @media screen and (max-width: 1100px) {
            width: 280px;
        }

        &::-webkit-scrollbar {
            width: 4px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 2px;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: #555;
        }

        .vti__search_box {
            margin: 0;
            padding: 16px 48px;
        }

        .vti__flag-wrapper {
            display: none;
        }
        .vti__dropdown-item {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 16px 48px;
            font-family: "ITC Avant Garde Gothic Std",serif;
            font-size: 16px;
            font-style: normal;
            font-weight: 300;
            line-height: 175%;
            color: #878c95;
            background-color: transparent;
            transition: all 0.3s;

            @media screen and (max-width: 1100px) {
                font-size: 12px;
            }

            strong {
                order: 2;
                font-weight: 300;
            }
            span {
                color: #202020;
                margin-right: 8px;
                order: 1;
            }

            &.highlighted {
                color: #202020;
                background-color: #e3f3ff;
            }
        }
    }
}
</style>
